import { useContext } from "react";
import { LocalUserChoices } from "@livekit/components-react";
import { VideoRoom } from "protogen/video_pb";
import {
  useCreateVideoRoom,
  useJoinVideoRoom,
  useEndVideoRoom,
} from "services/video";
import { VideoCallContext } from "components/context/VideoCallContext";

export const useVideoCall = () => {
  const context = useContext(VideoCallContext);
  const { request: createVideoRoom } = useCreateVideoRoom();
  const { request: joinVideoRoom } = useJoinVideoRoom();
  const { request: endVideoRoom } = useEndVideoRoom();

  if (!context) {
    throw new Error(
      "useVideoCall must be used within a VideoCallContextProvider",
    );
  }

  const {
    videoCallState,
    startVideoCall,
    endVideoCall,
    resetManuallyEnded,
    togglePipMode,
    toggleMinimized,
    updateUserChoices,
    updateConnectionState,
  } = context;

  const startCallWithFamily = async (
    familyRef: string,
    recordingEnabled = true,
    userChoices?: LocalUserChoices,
  ): Promise<VideoRoom | null> => {
    const createResp = await createVideoRoom({
      recordingEnabled,
      familyRef,
    });

    if (!createResp || !createResp.videoRoom) {
      throw new Error("Failed to create video room");
    }

    const joinResp = await joinVideoRoom({
      uuid: createResp.videoRoom.uuid,
      hostRejoin: false,
    });

    if (!joinResp || !joinResp.token) {
      throw new Error("Failed to get video room token");
    }

    startVideoCall(createResp.videoRoom, joinResp.token, userChoices);

    return createResp.videoRoom;
  };

  const joinVideoCall = async (
    uuid: string,
    userChoices?: LocalUserChoices,
  ): Promise<void> => {
    const joinResp = await joinVideoRoom({
      uuid,
      hostRejoin: false,
    });

    if (!joinResp || !joinResp.token || !joinResp.videoRoom) {
      throw new Error("Failed to join video room");
    }

    startVideoCall(joinResp.videoRoom, joinResp.token, userChoices);
    return;
  };

  const endCurrentVideoCall = async (): Promise<void> => {
    if (!videoCallState.activeVideoRoom) {
      return;
    }

    await endVideoRoom({ uuid: videoCallState.activeVideoRoom.uuid });
    endVideoCall();
  };

  return {
    videoCallState,
    isInCall: !!videoCallState.activeVideoRoom,
    isPipMode: videoCallState.isPipMode,
    isMinimized: videoCallState.isMinimized,
    isConnected: videoCallState.isConnected,
    startCallWithFamily,
    joinVideoCall,
    endCurrentVideoCall,
    togglePipMode,
    toggleMinimized,
    updateUserChoices,
    updateConnectionState,
    startVideoCall,
    endVideoCall,
    resetManuallyEnded,
  };
};
